import React from 'react';
import { MapPin, Phone, Mail, Clock, Calendar, MessageCircle, Car, Brain as Train, Navigation } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Contact = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();

  return (
    <section id="contact" className="section-modern relative">
      {/* Fond avec dégradé complexe */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"></div>
      
      {/* Effets lumineux */}
      <div className="absolute top-1/4 left-1/6 w-64 h-64 bg-yellow-400/5 rounded-full blur-3xl animate-float-elegant"></div>
      <div className="absolute bottom-1/4 right-1/6 w-48 h-48 bg-yellow-400/3 rounded-full blur-2xl animate-float-elegant" style={{animationDelay: '3s'}}></div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* En-tête */}
          <div
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-12 sm:mb-20"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light heading-modern mb-4 sm:mb-6 px-4">
              Nous <span className="heading-accent">Contacter</span>
            </h2>
            <div className="w-24 sm:w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-6 sm:mb-8"></div>
            <p className="text-base sm:text-lg md:text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed px-4">
              Votre projet musical mérite un accompagnement d'exception.
              Rencontrons-nous pour en discuter.
            </p>
          </div>

          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal grid lg:grid-cols-2 gap-16"
          >
            {/* Informations de contact */}
            <div className="space-y-8">
              <div className="glass-card interactive-card rounded-2xl p-8 light-effect">
                <div className="flex items-start space-x-6">
                  <MapPin className="h-8 w-8 text-yellow-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Notre Showroom</h3>
                    <p className="text-white/70 font-light leading-relaxed">
                      122, rue de l'Île Napoléon<br />
                      68170 RIXHEIM<br />
                      Haut-Rhin, Alsace
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card interactive-card rounded-2xl p-8 light-effect">
                <div className="flex items-start space-x-6">
                  <Car className="h-8 w-8 text-yellow-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Parking Gratuit</h3>
                    <p className="text-white/70 font-light leading-relaxed">
                      Places de parking gratuites disponibles directement devant le magasin pour votre confort.
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card interactive-card rounded-2xl p-8 light-effect">
                <div className="flex items-start space-x-6">
                  <Phone className="h-8 w-8 text-yellow-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Nous Appeler</h3>
                    <div className="space-y-2 text-white/70 font-light">
                      <p>Magasin : <a href="tel:+33389542003" className="hover:text-yellow-400 transition-colors">03 89 54 20 03</a></p>
                      <p>Mobile : <a href="tel:+33607971604" className="hover:text-yellow-400 transition-colors">06 07 97 16 04</a></p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card interactive-card rounded-2xl p-8 light-effect">
                <div className="flex items-start space-x-6">
                  <Mail className="h-8 w-8 text-yellow-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Nous Écrire</h3>
                    <p className="text-white/70 font-light">
                      <a href="mailto:musique.buchta@gmail.com" className="hover:text-yellow-400 transition-colors">
                        musique.buchta@gmail.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Horaires et services */}
            <div className="space-y-8">
              <div className="glass-card rounded-2xl p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <Clock className="h-8 w-8 text-yellow-400" />
                  <h3 className="text-xl font-semibold text-white">Horaires d'Ouverture</h3>
                </div>
                
                <div className="space-y-4 text-white/70 font-light">
                  {[
                    { day: "Lundi", hours: "Fermé" },
                    { day: "Mardi", hours: "Sur RDV" },
                    { day: "Mercredi", hours: "10h - 12h / 14h - 18h30" },
                    { day: "Jeudi", hours: "Sur RDV" },
                    { day: "Vendredi", hours: "Sur RDV" },
                    { day: "Samedi", hours: "10h - 12h / 14h - 18h30" },
                    { day: "Dimanche", hours: "Fermé" }
                  ].map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-white/10 last:border-b-0">
                      <span className="font-medium">{schedule.day}</span>
                      <span className="text-right">{schedule.hours}</span>
                    </div>
                  ))}
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <p className="text-sm text-white/60 italic">
                      Jean-Claude Buchta est souvent en clientèle ou en intervention.
                      Nous vous recommandons vivement de téléphoner avant de vous déplacer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <Calendar className="h-8 w-8 text-yellow-400" />
                  <h3 className="text-xl font-semibold text-white">Services Clés en Main</h3>
                </div>

                <p className="text-white/70 font-light leading-relaxed mb-4">
                  Accordage et expertise de pianos à domicile dans tout le Haut-Rhin.
                  Intervention sur rendez-vous pour un service personnalisé.
                </p>

                <div className="flex items-center space-x-2 text-yellow-400">
                  <MessageCircle className="h-5 w-5" />
                  <span className="font-light">Devis gratuit sur demande</span>
                </div>
              </div>
            </div>
          </div>

          {/* Carte interactive */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-light text-white mb-6">
                Nous <span className="heading-accent">Trouver</span>
              </h3>
              <p className="text-white/70 font-light text-lg">
                Visitez notre showroom au cœur de l'Alsace
              </p>
            </div>
            
            {/* Section avec adresse détaillée */}
            <div className="mt-12 glass-card rounded-3xl p-8 max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h4 className="text-2xl font-semibold text-white mb-4">Comment nous trouver</h4>
                  <div className="space-y-4 text-white/70">
                    <div className="flex items-start space-x-3">
                      <MapPin className="h-5 w-5 text-yellow-400 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-white">Adresse complète</p>
                        <p>122, rue de l'Île Napoléon<br />68170 RIXHEIM<br />Haut-Rhin, Alsace</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Car className="h-5 w-5 text-yellow-400 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-white">En voiture</p>
                        <p>Sortie A36 Rixheim<br />Parking disponible devant le magasin</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Train className="h-5 w-5 text-yellow-400 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-white">En transport</p>
                        <p>Proche de la gare de Rixheim<br />Lignes de bus locales</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <a
                    href="https://www.google.com/maps/search/122+rue+de+l'Île+Napoléon+68170+Rixheim"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-premium px-8 py-4 rounded-full font-semibold inline-flex items-center space-x-2"
                  >
                    <Navigation className="w-5 h-5" />
                    <span>Itinéraire GPS</span>
                  </a>
                </div>
              </div>
            </div>
            
          </div>
          {/* Call to action final */}
          <div className="text-center mt-20">
            <div className="glass-card rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-light text-white mb-6">
                Prêt à <span className="heading-accent">Commencer Votre Projet</span> ?
              </h3>
              <p className="text-white/70 font-light text-lg mb-8 leading-relaxed">
                Que ce soit pour l'achat d'un instrument, une location ou un service d'accordage, 
                nous sommes là pour vous accompagner avec expertise et passion.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+33389542003" 
                  className="btn-premium px-10 py-4 rounded-full font-semibold tracking-wide inline-block"
                >
                  Appeler Maintenant
                </a>
                <a 
                  href="mailto:musique.buchta@gmail.com" 
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-10 py-4 rounded-full font-light tracking-wide transition-all duration-300 inline-block"
                >
                  Envoyer un Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;